// Global Instructions Rule Applied!
//
// Configuration → Lookup Lists: generic manager for the reference lists still
// living in public.lookup_headers / lookup_details (Deal Type, Security Type,
// Funding Stage, Priority, Gender, …). Master–detail chrome copied from
// IndustriesPanel (headers table → one list's values).
//
// Disable-only v1 (owner decision 2026-08-26): values can be added, renamed,
// reordered and disabled — never hard-deleted. "Disabled" = soft-trashed: the
// value vanishes from every dropdown but existing records keep their
// reference, and it can be re-enabled here. The machine key (`value`) is
// read-only — code and data match on it. Categories whose labels are
// load-bearing SQL literals (e.g. Pipeline Type) are read-only end-to-end
// (the backend refuses their writes too).

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Input, Modal, Space, Switch, Table, Tag, Tooltip, Typography } from 'antd'
import { ArrowDownOutlined, ArrowLeftOutlined, ArrowUpOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import {
  type LookupListDetailRow,
  type LookupListHeaderRow
} from '../../../state_manager/data_models/lookup_lists_config_model.ts'
import {
  createLookupDetail,
  fetchLookupListDetails,
  fetchLookupLists,
  reorderLookupDetails,
  updateLookupDetail
} from '../services/lookupListsConfigService.ts'
import { refreshBoardConfigs } from '../services/refreshBoardConfigs.ts'

const { Title, Text } = Typography

// Mirror of the backend deny-list (LOOKUP_ADMIN_PROTECTED_CATEGORIES): shown
// read-only here; the backend refuses their writes regardless. Global Configs
// is system settings (login_domain, internal_company_id) found by label in
// code — renaming or disabling a row breaks login/team resolution silently.
const PROTECTED_CATEGORIES = new Set(['Pipeline Type', 'Global Configs'])

export default function LookupListsPanel() {
  const [headers, setHeaders] = useState<LookupListHeaderRow[]>([])
  const [loading, setLoading] = useState(false)

  // Master–detail: when set, show that list's values.
  const [detailHeader, setDetailHeader] = useState<LookupListHeaderRow | null>(null)
  const [details, setDetails] = useState<LookupListDetailRow[]>([])
  const [detailsLoading, setDetailsLoading] = useState(false)

  // Add / rename modal
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<LookupListDetailRow | null>(null)
  const [labelValue, setLabelValue] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [savingId, setSavingId] = useState<string | null>(null)

  const token = () => getAccessToken(store.getState().auth)

  const loadHeaders = useCallback(async () => {
    setLoading(true)
    try {
      setHeaders(await fetchLookupLists(token()))
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load lookup lists')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadHeaders()
  }, [loadHeaders])

  const loadDetails = useCallback(async (header: LookupListHeaderRow) => {
    setDetailsLoading(true)
    try {
      setDetails(await fetchLookupListDetails(header.lookup_headers__id, token()))
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load values')
    } finally {
      setDetailsLoading(false)
    }
  }, [])

  const openList = (header: LookupListHeaderRow) => {
    setDetailHeader(header)
    loadDetails(header)
  }

  const isProtected = !!detailHeader && PROTECTED_CATEGORIES.has(detailHeader.lookup_headers__category)

  const refreshAfterWrite = async () => {
    if (detailHeader) await loadDetails(detailHeader)
    await loadHeaders()
    refreshBoardConfigs()
  }

  const submitLabel = async () => {
    if (!detailHeader) return
    const label = labelValue.trim()
    if (!label) return
    setSubmitting(true)
    try {
      if (editing) {
        await updateLookupDetail(
          detailHeader.lookup_headers__id, editing.lookup_details__id, { label }, token())
        toast.success('Value renamed')
      } else {
        await createLookupDetail(detailHeader.lookup_headers__id, { label }, token())
        toast.success('Value added')
      }
      setModalOpen(false)
      await refreshAfterWrite()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSubmitting(false)
    }
  }

  const toggleDisabled = async (row: LookupListDetailRow, disabled: boolean) => {
    if (!detailHeader) return
    setSavingId(row.lookup_details__id)
    try {
      await updateLookupDetail(
        detailHeader.lookup_headers__id, row.lookup_details__id, { trashed: disabled }, token())
      await refreshAfterWrite()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Update failed')
      if (detailHeader) await loadDetails(detailHeader) // snap the switch back
    } finally {
      setSavingId(null)
    }
  }

  // Up/down = whole-order swap posted in one call (Email Categories precedent).
  const move = async (row: LookupListDetailRow, direction: -1 | 1) => {
    if (!detailHeader) return
    const live = details.filter((d) => !d.lookup_details__trashed)
    const idx = live.findIndex((d) => d.lookup_details__id === row.lookup_details__id)
    const swap = idx + direction
    if (idx < 0 || swap < 0 || swap >= live.length) return
    const order = [...live]
    ;[order[idx], order[swap]] = [order[swap], order[idx]]
    setSavingId(row.lookup_details__id)
    try {
      await reorderLookupDetails(
        detailHeader.lookup_headers__id, order.map((d) => d.lookup_details__id), token())
      await refreshAfterWrite()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Reorder failed')
    } finally {
      setSavingId(null)
    }
  }

  const headerColumns: ColumnsType<LookupListHeaderRow> = [
    {
      title: 'List',
      dataIndex: 'lookup_headers__category',
      key: 'category',
      sorter: (a, b) => a.lookup_headers__category.localeCompare(b.lookup_headers__category),
      defaultSortOrder: 'ascend',
      render: (category: string, row) => (
        <Space size='small'>
          <Button type='link' style={{ padding: 0 }} onClick={() => openList(row)}>
            {category}
          </Button>
          {PROTECTED_CATEGORIES.has(category) && (
            <Tooltip title='Values referenced by name in system logic — read-only'>
              <Tag>system</Tag>
            </Tooltip>
          )}
        </Space>
      )
    },
    {
      title: 'Values',
      dataIndex: 'prog__detail_count',
      key: 'count',
      width: 120,
      align: 'right',
      sorter: (a, b) => a.prog__detail_count - b.prog__detail_count,
      render: (count: number) => <Text type='secondary'>{count}</Text>
    }
  ]

  const liveIds = details.filter((d) => !d.lookup_details__trashed).map((d) => d.lookup_details__id)

  const detailColumns: ColumnsType<LookupListDetailRow> = [
    {
      title: 'Label',
      dataIndex: 'lookup_details__label',
      key: 'label',
      render: (label: string, row) => (
        <Text type={row.lookup_details__trashed ? 'secondary' : undefined} delete={row.lookup_details__trashed}>
          {label}
        </Text>
      )
    },
    {
      title: 'Key',
      dataIndex: 'lookup_details__value',
      key: 'value',
      width: 260,
      render: (value: string | null | undefined) =>
        value ? <Text type='secondary' style={{ fontSize: 12 }}>{value}</Text> : <Text type='secondary'>—</Text>
    },
    {
      title: 'Order',
      key: 'order',
      width: 90,
      render: (_, row) =>
        row.lookup_details__trashed || isProtected ? null : (
          <Space size={0}>
            <Button
              type='text' size='small' icon={<ArrowUpOutlined />}
              disabled={liveIds[0] === row.lookup_details__id || savingId !== null}
              onClick={() => move(row, -1)}
            />
            <Button
              type='text' size='small' icon={<ArrowDownOutlined />}
              disabled={liveIds[liveIds.length - 1] === row.lookup_details__id || savingId !== null}
              onClick={() => move(row, 1)}
            />
          </Space>
        )
    },
    {
      title: 'Enabled',
      key: 'enabled',
      width: 90,
      render: (_, row) => (
        <Switch
          size='small'
          checked={!row.lookup_details__trashed}
          disabled={isProtected}
          loading={savingId === row.lookup_details__id}
          onChange={(checked) => toggleDisabled(row, !checked)}
        />
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 70,
      render: (_, row) =>
        isProtected ? null : (
          <Tooltip title='Rename'>
            <Button
              type='text' size='small' icon={<EditOutlined />}
              onClick={() => {
                setEditing(row)
                setLabelValue(row.lookup_details__label)
                setModalOpen(true)
              }}
            />
          </Tooltip>
        )
    }
  ]

  if (detailHeader) {
    return (
      <div className='flex flex-col h-full min-h-0'>
        <div className='flex items-center justify-between flex-wrap gap-2 mb-3'>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => setDetailHeader(null)}>
              All lists
            </Button>
            <Title level={4} style={{ margin: 0 }}>
              {detailHeader.lookup_headers__category}
            </Title>
          </Space>
          {!isProtected && (
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null)
                setLabelValue('')
                setModalOpen(true)
              }}
            >
              New Value
            </Button>
          )}
        </div>

        {isProtected && (
          <Alert
            type='info'
            showIcon
            style={{ marginBottom: 12 }}
            message='These values are referenced by name in system logic and are read-only here.'
          />
        )}

        <div className='flex-1 min-h-0 overflow-auto'>
          <Table
            size='small'
            rowKey='lookup_details__id'
            columns={detailColumns}
            dataSource={details}
            loading={detailsLoading}
            pagination={false}
          />
        </div>

        <Modal
          title={editing ? 'Rename value' : 'New value'}
          open={modalOpen}
          okText={editing ? 'Rename' : 'Add'}
          okButtonProps={{ disabled: !labelValue.trim() }}
          confirmLoading={submitting}
          onOk={submitLabel}
          onCancel={() => setModalOpen(false)}
          destroyOnClose
        >
          <Input
            value={labelValue}
            onChange={(e) => setLabelValue(e.target.value)}
            onPressEnter={submitLabel}
            placeholder='Label'
            autoFocus
          />
        </Modal>
      </div>
    )
  }

  return (
    <div className='flex flex-col h-full min-h-0'>
      <div className='flex items-center justify-between flex-wrap gap-2 mb-3'>
        <Title level={4} style={{ margin: 0 }}>
          Lookup Lists
        </Title>
      </div>
      <Text type='secondary' style={{ marginBottom: 12 }}>
        Reference lists used across forms and filters. Click a list to manage its
        values — disabling a value hides it from new selections without touching
        existing records.
      </Text>
      <div className='flex-1 min-h-0 overflow-auto'>
        <Table
          size='small'
          rowKey='lookup_headers__id'
          columns={headerColumns}
          dataSource={headers}
          loading={loading}
          pagination={false}
        />
      </div>
    </div>
  )
}
